import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Ban, Save } from 'lucide-react';
import { Header } from '../components/Header';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminActionModal } from '../components/AdminActionModal';
import { getAdminProviderDetails, blockProvider, updateAdminNote } from '../services/api';
import { AdminProviderDetails as AdminProviderDetailsType } from '../types';
import { ROUTES } from '../constants';
import { useAuth } from '../context/AuthContext';

export const AdminProviderDetails: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const providerId = searchParams.get('id');

  if (user?.role !== 'admin') {
    return <Navigate to={ROUTES.HOME_PAGE} replace />;
  }

  const [provider, setProvider] = useState<AdminProviderDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    actionType: 'block' | 'note';
    onConfirm: (data?: string) => void;
  } | null>(null);

  useEffect(() => {
    if (providerId) {
      fetchProviderDetails();
    } else {
      navigate(ROUTES.ADMIN_DASHBOARD);
    }
  }, [providerId]);

  const fetchProviderDetails = async () => {
    if (!providerId) return;

    setLoading(true);
    const response = await getAdminProviderDetails({ providerId: parseInt(providerId) });

    if (response.success && response.provider) {
      setProvider(response.provider);
      setAdminNote(response.provider.adminNote || '');
    } else {
      alert(response.error || 'Failed to fetch provider details');
      navigate(ROUTES.ADMIN_DASHBOARD);
    }
    setLoading(false);
  };

  const handleBlock = () => {
    const isCurrentlyBlocked = provider?.isBlocked || false;
    setModalConfig({
      title: isCurrentlyBlocked ? 'Unblock Provider' : 'Block Provider',
      message: isCurrentlyBlocked
        ? 'Are you sure you want to unblock this provider?'
        : 'Are you sure you want to block this provider?',
      actionType: 'block',
      onConfirm: async (note?: string) => {
        setActionLoading(true);
        const response = await blockProvider({
          providerId: parseInt(providerId!),
          blocked: !isCurrentlyBlocked,
          note
        });
        setActionLoading(false);
        setShowModal(false);

        if (response.success) {
          alert(isCurrentlyBlocked ? 'Provider unblocked successfully' : 'Provider blocked successfully');
          fetchProviderDetails();
        } else {
          alert(response.error || 'Failed to update provider status');
        }
      },
    });
    setShowModal(true);
  };

  const handleSaveNote = async () => {
    if (!providerId) return;

    setActionLoading(true);
    const response = await updateAdminNote({
      providerId: parseInt(providerId),
      adminNote,
    });
    setActionLoading(false);

    if (response.success) {
      alert('Note updated successfully');
    } else {
      alert(response.error || 'Failed to update note');
    }
  };

  const getStatusBadge = () => {
    if (!provider) return null;

    if (provider.isBlocked) {
      return <Badge variant="error">Blocked</Badge>;
    }
    return <Badge variant="success">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header theme="blue" />
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header theme="blue" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-500">Provider not found</p>
        </div>
      </div>
    );
  }

  const typeOfCareArray = Array.isArray(provider.typeOfCare)
    ? provider.typeOfCare
    : [provider.typeOfCare];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header theme="blue" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{provider.name}</h1>
              <p className="text-gray-600 mt-1">Provider ID: {provider.providerId}</p>
            </div>
            {getStatusBadge()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Email</h3>
              <p className="text-gray-900">{provider.email}</p>
            </div>

            {provider.phone && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Phone</h3>
                <p className="text-gray-900">{provider.phone}</p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Type of Care</h3>
              <div className="flex flex-wrap gap-2">
                {typeOfCareArray.map((type, index) => (
                  <Badge key={index} variant="info">
                    {type}
                  </Badge>
                ))}
              </div>
            </div>

            {provider.address && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Address</h3>
                <p className="text-gray-900">
                  {provider.address}
                  {provider.city && `, ${provider.city}`}
                  {provider.postalCode && ` ${provider.postalCode}`}
                </p>
              </div>
            )}

            {provider.services && provider.services.length > 0 && (
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Services</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.services.map((service, index) => (
                    <Badge key={index} variant="primary">
                      {service}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {provider.credentials && provider.credentials.length > 0 && (
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Credentials</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.credentials.map((credential, index) => (
                    <Badge key={index} variant="success">
                      {credential}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Admin Note</h3>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none"
              placeholder="Add internal notes about this provider..."
            />
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveNote}
                disabled={actionLoading}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Note
              </Button>
            </div>
          </div>

          <div className="border-t pt-6 flex flex-wrap gap-3">
            <Button
              variant={provider.isBlocked ? 'primary' : 'danger'}
              onClick={handleBlock}
              disabled={actionLoading}
            >
              <Ban className="h-4 w-4 mr-2" />
              {provider.isBlocked ? 'Unblock Provider' : 'Block Provider'}
            </Button>
          </div>
        </div>
      </div>

      {modalConfig && (
        <AdminActionModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={modalConfig.onConfirm}
          title={modalConfig.title}
          message={modalConfig.message}
          actionType={modalConfig.actionType}
          loading={actionLoading}
        />
      )}
    </div>
  );
};
